import { Flex } from '@mantine/core';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { ChevronRight, TriangleDown } from 'baseui/icon';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { HeadingMedium, LabelMedium, LabelSmall } from 'baseui/typography';
import { BellSVG, BroomIcon, LockSVG, LogoutSVG, UserSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import clearStorage from 'common/utils/storage';
import { setLanguage } from 'components/common/language-storage';
import LoadingComponent from 'components/common/loading';
import notification from 'components/common/notification';
import PaginationLoader from 'components/common/pagination-loader';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import useLogout from 'hooks/use-logout';
import useNavigation from 'hooks/use-navigation';
import useNotifications from 'hooks/use-notifications';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import HamburgerButton from './hamberger-button';
import { SideNavigationRef } from './side-navigation/side-navigation';
import { HeaderContainer, HeaderLogoContainer } from './styles';

interface HeaderProps {
  sideNavigationRef: React.RefObject<SideNavigationRef>;
}

export default function Header(props: HeaderProps) {
  const { t } = useTranslation();
  const { locale, push, replace, pathname } = useRouter();
  const { navigate } = useNavigation();
  const handleLogout = useLogout();
  const {
    hasMore,
    notifications,
    readAllLoading,
    readNotificationId,
    isFetching,
    isFetchingNextPage,
    unreadNotificationsCount,
    onLoadMore,
    onReadAll,
    onReadNotification,
  } = useNotifications();

  const languages = React.useMemo(() => {
    return [
      {
        label: 'default:id',
        locale: 'id',
      },
      {
        label: 'default:en',
        locale: 'en',
      },
    ];
  }, []);

  const notificationRef = React.useRef<HTMLDivElement>(null);

  return (
    <HeaderContainer>
      <HamburgerButton sideNavigationRef={props.sideNavigationRef} />
      <HeaderLogoContainer
        style={{
          color: 'black',
          fontWeight: 'bold',
          fontSize: 24,
          fontStyle: 'italic',
        }}
      >
        <Image src="/assets/logo.png" alt="logo" height={52.5} width={140} />
      </HeaderLogoContainer>
      <StatefulPopover
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        focusLock
        overrides={{
          Body: {
            style: {
              borderRadius: '4px',
              overflow: 'hidden',
              zIndex: 199,
            },
          },
        }}
        placement={PLACEMENT.bottomRight}
        content={({ close }) => (
          <NotificationContainer ref={notificationRef}>
            {notifications?.length === 0 && !isFetching && (
              <Flex
                direction="column"
                gap="md"
                justify="center"
                align="center"
                p="lg"
              >
                <BellSVG color={stylesColors.font} size={50} />
                <HeadingMedium>{t('common:no_notification')}</HeadingMedium>
                <LabelMedium>{t('common:no_notification_message')}</LabelMedium>
              </Flex>
            )}

            {notifications?.length > 0 && (
              <>
                <Flex justify="flex-end" p={8}>
                  <Button
                    buttonType="tertiary"
                    onClick={() => onReadAll(close)}
                    isLoading={readAllLoading}
                  >
                    Read All
                  </Button>
                </Flex>
                {notifications?.map((notification) => {
                  const readNotificationLoading =
                    notification.id === readNotificationId;
                  return (
                    <NotificationItem
                      key={notification.id}
                      onClick={() => onReadNotification(notification, close)}
                      isRead={!!notification.readAt}
                    >
                      <Flex justify="space-between" direction="row" w="100%">
                        <Flex direction="column" gap="sm">
                          <LabelMedium>
                            {notification.data.subject[locale!]}
                          </LabelMedium>
                          <LabelSmall>
                            {notification.data.body[locale!]}
                          </LabelSmall>
                        </Flex>
                        {readNotificationLoading ? (
                          <LoadingSpinner />
                        ) : (
                          <ChevronRight color={stylesColors.font} size={24} />
                        )}
                      </Flex>
                    </NotificationItem>
                  );
                })}
                {hasMore && (
                  <LoadMoreButton onClick={() => onLoadMore()}>
                    {isFetchingNextPage ? (
                      <LoadingComponent />
                    ) : (
                      t('common:load_more')
                    )}
                  </LoadMoreButton>
                )}
                {(!isFetchingNextPage || !isFetching) && (
                  <PaginationLoader
                    hasNextPage={hasMore!}
                    loadNextPage={onLoadMore}
                    root={notificationRef}
                  />
                )}
              </>
            )}
          </NotificationContainer>
        )}
      >
        <HeaderProfilePicture onClick={() => {}}>
          <NotificationIcon>
            <BellSVG color={stylesColors.font} size={28} />
            {!!unreadNotificationsCount && (
              <NotificationBadge>
                {(unreadNotificationsCount || 0) > 99
                  ? '99+'
                  : unreadNotificationsCount}
              </NotificationBadge>
            )}
          </NotificationIcon>
        </HeaderProfilePicture>
      </StatefulPopover>
      <StatefulPopover
        focusLock
        overrides={{
          Body: {
            style: {
              borderRadius: '4px',
              overflow: 'hidden',
              zIndex: 199,
            },
          },
        }}
        placement={PLACEMENT.bottomRight}
        content={({ close }) => (
          <ProfileMenuContainer>
            {languages.map((language) => {
              return (
                <ProfileMenuItem
                  key={language.locale}
                  onClick={() => {
                    push(pathname, pathname, { locale: language.locale });
                    setLanguage(language.locale);
                    close();
                  }}
                >
                  <LabelMedium>{t(language.label)}</LabelMedium>
                </ProfileMenuItem>
              );
            })}
          </ProfileMenuContainer>
        )}
      >
        <HeaderProfilePicture onClick={() => {}}>
          <Text variant="body1Regular">{locale?.toUpperCase()}</Text>
          <TriangleDown size={24} color={stylesColors.font} />
        </HeaderProfilePicture>
      </StatefulPopover>
      <StatefulPopover
        focusLock
        overrides={{
          Body: {
            style: {
              borderRadius: '4px',
              overflow: 'hidden',
              zIndex: 199,
            },
          },
        }}
        placement={PLACEMENT.bottomRight}
        content={({ close }) => (
          <ProfileMenuContainer>
            <ProfileMenuItem
              onClick={() => {
                navigate(NavigationRoutes.ChangePassword);
                close();
              }}
            >
              <LockSVG color="black" />
              <div style={{ marginRight: 8 }} />
              <LabelMedium>{t('common:change_password')}</LabelMedium>
            </ProfileMenuItem>
            <ProfileMenuItem
              onClick={() =>
                handleLogout(() => {
                  close();
                  replace('/login');
                })
              }
            >
              <LogoutSVG color="black" size={24} />
              <div style={{ marginRight: 8 }} />
              <LabelMedium>{t('common:logout')}</LabelMedium>
            </ProfileMenuItem>
            <ProfileMenuItem
              onClick={() => {
                clearStorage();
                notification.success({
                  message: t('common:storage_cleared'),
                  autoHideDuration: 2000,
                });
                close();
              }}
            >
              <BroomIcon color="black" size={24} />
              <div style={{ marginRight: 8 }} />
              <LabelMedium>{t('common:clear_storage')}</LabelMedium>
            </ProfileMenuItem>
          </ProfileMenuContainer>
        )}
      >
        <HeaderProfilePicture onClick={() => {}}>
          <UserSVG color={stylesColors.font} size={28} />
          <TriangleDown size={24} color={stylesColors.font} />
        </HeaderProfilePicture>
      </StatefulPopover>
    </HeaderContainer>
  );
}

const ProfileMenuContainer = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
}));

const ProfileMenuItem = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  flex: 1,
  padding: '12px 20px',
  alignItems: 'center',
  cursor: 'pointer',
  ':hover': {
    background: '#00000018',
  },
}));

const HeaderProfilePicture = themedStyled('div', ({ $theme }) => ({
  marginRight: '16px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
}));

const NotificationIcon = themedStyled('div', ({ $theme }) => ({
  position: 'relative',
  display: 'inline-block',
}));

const NotificationBadge = themedStyled('span', ({ $theme }) => ({
  position: 'absolute',
  top: '-10px',
  right: '-10px',
  backgroundColor: '#D44333',
  color: '#FFFFFF',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '12px',
}));

const NotificationContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>((props, ref) => (
  <div
    ref={ref}
    style={{
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      maxWidth: '400px',
      maxHeight: '80vh',
      overflow: 'auto',
      background: '#FFFFFF',
    }}
  >
    {props.children}
  </div>
));

const NotificationItem = ({
  children,
  isRead,
  onClick,
}: {
  children: React.ReactNode;
  isRead: boolean;
  onClick: () => void;
}) => {
  const [isHover, setIsHover] = React.useState(false);
  const style = (isRead: boolean) => ({
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    background: isRead ? (isHover ? '#EAF0F9' : '#FFFFFF') : '#EAF0F9',
    boxShadow: isHover ? '0px 0px 8px rgba(0, 0, 0, 0.12)' : undefined,
  });

  return (
    <div
      style={style(isRead)}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {children}
    </div>
  );
};

const LoadMoreButton = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  padding: '12px 20px',
  justifyContent: 'center',
  background: '#FFFFFF',
  cursor: 'pointer',
  ':hover': {
    background: '#EAF0F9',
  },
}));
